<script setup>
/* global axios */
/**
 * This component handles parsing and sending WhatsApp message templates.
 * It works as follows:
 * 1. Displays the template text with variable placeholders.
 * 2. Generates input fields for each variable in the template.
 * 3. Validates that all variables are filled before sending.
 * 4. Replaces placeholders with user-provided values.
 * 5. Emits events to send the processed message or reset the template.
 */
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';

import { isWhatsAppComplete } from '@chatwoot/utils';
import Input from 'dashboard/components-next/input/Input.vue';
import {
  buildTemplateParameters,
  allKeysRequired,
  DEFAULT_LANGUAGE,
  DEFAULT_CATEGORY,
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
  renderTemplatePreview,
  buildCurrencyParam,
  isCurrencyParam,
  flattenBodyForPreview,
  CURRENCY_CODES,
  DEFAULT_CURRENCY_CODE,
} from 'dashboard/helper/templateHelper';

const props = defineProps({
  template: {
    type: Object,
    default: () => ({}),
    validator: value => {
      if (!value || typeof value !== 'object') return false;
      if (!value.components || !Array.isArray(value.components)) return false;
      return true;
    },
  },
  sendRenderedContent: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['sendMessage', 'resetTemplate', 'back']);

const { t } = useI18n();
const route = useRoute();

// Chaves Pix salvas da conta (para o modo "Código Pix" da variável de corpo).
const pixKeys = ref([]);
const loadPixKeys = async () => {
  try {
    const { data } = await axios.get(
      `/api/v1/accounts/${route.params.accountId}/kanban/pix_keys`
    );
    pixKeys.value = data?.data || [];
  } catch (error) {
    pixKeys.value = [];
  }
};

const processedParams = ref({});

// Per body-variable UI state so a variable can be sent as plain text (default)
// or as a typed `currency` parameter (the "value/Pix amount" case). The stored
// value in processedParams.body[key] stays a string for text and becomes the
// Meta currency object for currency.
const bodyParamMeta = ref({});

const ensureBodyMeta = () => {
  const meta = {};
  Object.keys(processedParams.value.body || {}).forEach(key => {
    const current = processedParams.value.body[key];
    meta[key] = isCurrencyParam(current)
      ? { mode: 'currency', amount: '', code: current.code || DEFAULT_CURRENCY_CODE, pixKeyId: '' }
      : { mode: 'text', amount: '', code: DEFAULT_CURRENCY_CODE, pixKeyId: '' };
  });
  bodyParamMeta.value = meta;
};

const updateBodyCurrency = key => {
  const { amount, code } = bodyParamMeta.value[key] || {};
  // Empty/invalid amount → keep '' so completeness validation still blocks send.
  processedParams.value.body[key] = buildCurrencyParam(amount, code) || '';
};

// Modo "Código Pix": gera o copia-e-cola no backend a partir de uma chave salva + valor.
const updateBodyPix = async key => {
  const { pixKeyId, amount } = bodyParamMeta.value[key] || {};
  if (!pixKeyId || !amount) {
    processedParams.value.body[key] = '';
    return;
  }
  try {
    const { data } = await axios.post(
      `/api/v1/accounts/${route.params.accountId}/kanban/pix_keys/${pixKeyId}/br_code`,
      { amount }
    );
    processedParams.value.body[key] = data?.code || '';
  } catch (error) {
    processedParams.value.body[key] = '';
  }
};

const onBodyModeChange = key => {
  const mode = bodyParamMeta.value[key].mode;
  if (mode === 'currency') {
    updateBodyCurrency(key);
  } else if (mode === 'pix') {
    updateBodyPix(key);
  } else {
    processedParams.value.body[key] = '';
  }
};

const onBodyCurrencyAmount = (key, value) => {
  bodyParamMeta.value[key].amount = value;
  updateBodyCurrency(key);
};

const onBodyPixKey = (key, value) => {
  bodyParamMeta.value[key].pixKeyId = value;
  updateBodyPix(key);
};

const onBodyPixAmount = (key, value) => {
  bodyParamMeta.value[key].amount = value;
  updateBodyPix(key);
};

const bodyTextValue = key => {
  const value = processedParams.value.body?.[key];
  return typeof value === 'string' ? value : '';
};

const languageLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.LANGUAGE')}: ${props.template.language || DEFAULT_LANGUAGE}`;
});

const categoryLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.CATEGORY')}: ${props.template.category || DEFAULT_CATEGORY}`;
});

const headerComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.HEADER);
});

const bodyComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.BODY);
});

const bodyText = computed(() => {
  return bodyComponent.value?.text || '';
});

const headerText = computed(() => {
  return headerComponent.value?.format === 'TEXT'
    ? headerComponent.value?.text || ''
    : '';
});

const hasMediaHeader = computed(() =>
  MEDIA_FORMATS.includes(headerComponent.value?.format)
);

const formatType = computed(() => {
  const format = headerComponent.value?.format;
  return format ? format.charAt(0) + format.slice(1).toLowerCase() : '';
});

const isDocumentTemplate = computed(() => {
  return headerComponent.value?.format?.toLowerCase() === 'document';
});

const hasBodyVariables = computed(() => {
  return bodyText.value?.match(/{{([^}]+)}}/g) !== null;
});

const hasTextHeaderVariables = computed(() => {
  return headerText.value?.match(/{{([^}]+)}}/g) !== null;
});

const hasVariables = computed(
  () => hasBodyVariables.value || hasTextHeaderVariables.value
);

const renderedHeader = computed(() => {
  return renderTemplatePreview(
    headerText.value,
    processedParams.value.header || {}
  );
});

const renderedTemplate = computed(() => {
  return renderTemplatePreview(
    bodyText.value,
    flattenBodyForPreview(processedParams.value.body || {})
  );
});

// Completeness validation is shared with the mobile app via @chatwoot/utils.
const isFormInvalid = computed(
  () => !isWhatsAppComplete(props.template, processedParams.value)
);

const v$ = useVuelidate(
  {
    processedParams: {
      requiredIfKeysPresent: requiredIf(hasVariables),
      allKeysRequired,
    },
  },
  { processedParams }
);

const initializeTemplateParameters = () => {
  processedParams.value = buildTemplateParameters(
    props.template,
    hasMediaHeader.value
  );
  ensureBodyMeta();
};

const updateMediaUrl = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_url = value;
};

const updateMediaName = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_name = value;
};

const sendMessage = () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  const { name, category, language, namespace } = props.template;

  const payload = {
    message: props.sendRenderedContent
      ? renderedTemplate.value
      : bodyText.value,
    pendingMessageContent: renderedTemplate.value,
    templateParams: {
      name,
      category,
      language,
      namespace,
      content_mode: props.sendRenderedContent ? 'rendered' : 'raw_template',
      processed_params: processedParams.value,
    },
  };
  emit('sendMessage', payload);
};

const resetTemplate = () => {
  emit('resetTemplate');
};

const goBack = () => {
  emit('back');
};

onMounted(() => {
  initializeTemplateParameters();
  loadPixKeys();
});

watch(
  () => props.template,
  () => {
    initializeTemplateParameters();
    v$.value.$reset();
  },
  { deep: true }
);

defineExpose({
  processedParams,
  hasVariables,
  hasMediaHeader,
  isDocumentTemplate,
  headerComponent,
  renderedHeader,
  renderedTemplate,
  isFormInvalid,
  v$,
  updateMediaUrl,
  updateMediaName,
  sendMessage,
  resetTemplate,
  goBack,
});
</script>

<template>
  <div>
    <div class="flex flex-col gap-4 p-4 mb-4 rounded-lg bg-n-alpha-black2">
      <div class="flex justify-between items-center">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ template.name }}
        </h3>
        <span class="text-xs text-n-slate-11">
          {{ languageLabel }}
        </span>
      </div>

      <div class="flex flex-col gap-2">
        <div class="rounded-md">
          <div
            v-if="renderedHeader"
            class="mb-2 text-sm font-medium whitespace-pre-wrap text-n-slate-12"
          >
            {{ renderedHeader }}
          </div>
          <div class="text-sm whitespace-pre-wrap text-n-slate-12">
            {{ renderedTemplate }}
          </div>
        </div>
      </div>

      <div class="text-xs text-n-slate-11">
        {{ categoryLabel }}
      </div>
    </div>

    <div v-if="hasVariables || hasMediaHeader">
      <div v-if="hasMediaHeader" class="mb-4">
        <p class="mb-2.5 text-sm font-semibold">
          {{
            $t('WHATSAPP_TEMPLATES.PARSER.MEDIA_HEADER_LABEL', {
              type: formatType,
            }) || `${formatType} Header`
          }}
        </p>
        <div class="flex items-center mb-2.5">
          <Input
            :model-value="processedParams.header?.media_url || ''"
            type="url"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.MEDIA_URL_LABEL', {
                type: formatType,
              })
            "
            @update:model-value="updateMediaUrl"
          />
        </div>
        <div v-if="isDocumentTemplate" class="flex items-center mb-2.5">
          <Input
            :model-value="processedParams.header?.media_name || ''"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.DOCUMENT_NAME_PLACEHOLDER')
            "
            @update:model-value="updateMediaName"
          />
        </div>
      </div>

      <!-- Text Header Variables Section -->
      <div v-if="hasTextHeaderVariables && processedParams.header">
        <p class="mb-2.5 text-sm font-semibold">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.HEADER_VARIABLES_LABEL') }}
        </p>
        <div
          v-for="(variable, key) in processedParams.header"
          :key="`header-${key}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.header[key]"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER', {
                variable: key,
              })
            "
          />
        </div>
      </div>

      <!-- Body Variables Section -->
      <div v-if="processedParams.body">
        <p class="mb-2.5 text-sm font-semibold">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_LABEL') }}
        </p>
        <div
          v-for="(variable, key) in processedParams.body"
          :key="`body-${key}`"
          class="mb-2.5"
        >
          <div class="flex gap-2 items-center">
            <select
              v-if="bodyParamMeta[key]"
              v-model="bodyParamMeta[key].mode"
              class="py-2 px-2 text-sm rounded-lg border outline-none bg-n-alpha-black2 border-n-weak text-n-slate-12"
              @change="onBodyModeChange(key)"
            >
              <option value="text">
                {{ $t('WHATSAPP_TEMPLATES.PARSER.TYPE_TEXT') }}
              </option>
              <option value="currency">
                {{ $t('WHATSAPP_TEMPLATES.PARSER.TYPE_CURRENCY') }}
              </option>
              <option v-if="pixKeys.length" value="pix">
                {{ $t('WHATSAPP_TEMPLATES.PARSER.TYPE_PIX') }}
              </option>
            </select>

            <!-- Currency: code + amount -->
            <template
              v-if="bodyParamMeta[key] && bodyParamMeta[key].mode === 'currency'"
            >
              <select
                v-model="bodyParamMeta[key].code"
                class="py-2 px-2 text-sm rounded-lg border outline-none bg-n-alpha-black2 border-n-weak text-n-slate-12"
                @change="updateBodyCurrency(key)"
              >
                <option v-for="code in CURRENCY_CODES" :key="code" :value="code">
                  {{ code }}
                </option>
              </select>
              <Input
                :model-value="bodyParamMeta[key].amount"
                type="text"
                inputmode="decimal"
                class="flex-1"
                :placeholder="
                  t('WHATSAPP_TEMPLATES.PARSER.CURRENCY_PLACEHOLDER')
                "
                @update:model-value="value => onBodyCurrencyAmount(key, value)"
              />
            </template>

            <!-- Código Pix: chave salva + valor -->
            <template
              v-else-if="bodyParamMeta[key] && bodyParamMeta[key].mode === 'pix'"
            >
              <select
                :value="bodyParamMeta[key].pixKeyId"
                class="py-2 px-2 text-sm rounded-lg border outline-none bg-n-alpha-black2 border-n-weak text-n-slate-12"
                @change="event => onBodyPixKey(key, event.target.value)"
              >
                <option value="">
                  {{ $t('WHATSAPP_TEMPLATES.PARSER.PIX_KEY_PLACEHOLDER') }}
                </option>
                <option v-for="pk in pixKeys" :key="pk.id" :value="pk.id">
                  {{ pk.label || pk.pix_key }}
                </option>
              </select>
              <Input
                :model-value="bodyParamMeta[key].amount"
                type="text"
                inputmode="decimal"
                class="flex-1"
                :placeholder="t('WHATSAPP_TEMPLATES.PARSER.CURRENCY_PLACEHOLDER')"
                @update:model-value="value => onBodyPixAmount(key, value)"
              />
            </template>

            <!-- Text (default) -->
            <Input
              v-else
              :model-value="bodyTextValue(key)"
              type="text"
              class="flex-1"
              :placeholder="
                t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER', {
                  variable: key,
                })
              "
              @update:model-value="value => (processedParams.body[key] = value)"
            />
          </div>

          <p
            v-if="
              bodyParamMeta[key] &&
              bodyParamMeta[key].mode === 'currency' &&
              isCurrencyParam(processedParams.body[key])
            "
            class="mt-1 text-xs text-n-slate-11"
          >
            {{ processedParams.body[key].fallback_value }} ·
            amount_1000={{ processedParams.body[key].amount_1000 }}
          </p>

          <p
            v-if="
              bodyParamMeta[key] &&
              bodyParamMeta[key].mode === 'pix' &&
              typeof processedParams.body[key] === 'string' &&
              processedParams.body[key]
            "
            class="mt-1 text-xs break-all text-n-slate-11"
          >
            {{ $t('WHATSAPP_TEMPLATES.PARSER.PIX_GENERATED') }}:
            {{ processedParams.body[key].slice(0, 44)
            }}{{ processedParams.body[key].length > 44 ? '…' : '' }}
          </p>
        </div>
      </div>

      <!-- Button Variables Section -->
      <div v-if="processedParams.buttons">
        <p class="mb-2.5 text-sm font-semibold">
          {{ t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETERS') }}
        </p>
        <div
          v-for="(button, index) in processedParams.buttons"
          :key="`button-${index}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.buttons[index].parameter"
            type="text"
            class="flex-1"
            :placeholder="t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETER')"
          />
        </div>
      </div>
      <p
        v-if="v$.$dirty && v$.$invalid"
        class="p-2.5 text-center rounded-md bg-n-ruby-9/20 text-n-ruby-9"
      >
        {{ $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}
      </p>
    </div>

    <slot
      name="actions"
      :send-message="sendMessage"
      :reset-template="resetTemplate"
      :go-back="goBack"
      :is-valid="!v$.$invalid"
      :disabled="isFormInvalid"
    />
  </div>
</template>
